import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ShareModal from "@/components/ShareModal";
import BookingModal from "@/components/BookingModal";
import UpgradeModal from "@/components/UpgradeModal";
import DisclosureGroup from "@/components/summary/DisclosureGroup";
import { buildTabSections } from "@/components/summary/reportSections";
import SummarySidebar from "@/components/summary/SummarySidebar";
import ResilienceScore from "@/components/ResilienceScore";
import { LoadingPhase, GeneratingPhase, FailedPhase } from "@/components/summary/SummaryPhases";
import { FileText, Share2, Calendar, Star, ChevronDown } from "lucide-react";
import { getAssessment, getReport, generateReport, getFeedbackToken } from "@/lib/api";
import { useAccess, pollPaymentStatus } from "@/lib/config";
import { DISCLAIMER_TEXT } from "@/lib/constants";

const MODE_LABEL = { solopreneur: "Solopreneur", sme: "SME", corporate: "Corporate Department" };

const REPORT_TABS = [
  { key: "executive", label: "Executive" },
  { key: "risks", label: "Risks Interrogated" },
  { key: "scenarios", label: "Risk Scenarios" },
  { key: "dependencies", label: "Dependencies" },
  { key: "playbooks", label: "Playbooks" },
  { key: "vendor", label: "Vendor Recovery" },
  { key: "comms", label: "Communications" },
];

export default function Summary() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [report, setReport] = useState(null);
  const [insights, setInsights] = useState([]);
  const [readiness, setReadiness] = useState({});
  const [mode, setMode] = useState("sme");
  const [shareOpen, setShareOpen] = useState(false);
  const [consultantOpen, setConsultantOpen] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [phase, setPhase] = useState("loading"); // loading | generating | ready | failed
  const [tab, setTab] = useState(() => {
    const t = searchParams.get("tab");
    return t && t !== "action-plan" ? t : "executive";
  });
  const [discOpen, setDiscOpen] = useState(false);
  const [pollSeconds, setPollSeconds] = useState(0);
  const [reportError, setReportError] = useState(null);
  const pollRef = useRef(null);
  const { access, refresh: refreshAccess } = useAccess(id);

  // Handle Stripe return — ?stripe_session_id=... means we just came back from checkout.
  useEffect(() => {
    const sid = searchParams.get("stripe_session_id");
    const cancelled = searchParams.get("stripe_cancelled");
    if (cancelled) {
      toast.info("Checkout cancelled — your brief stays in DRAFT mode.");
      // Clean URL
      const next = new URLSearchParams(searchParams);
      next.delete("stripe_cancelled");
      setSearchParams(next, { replace: true });
      return;
    }
    if (!sid) return;
    toast.message("Confirming payment…");
    (async () => {
      const result = await pollPaymentStatus(sid, {
        onUpdate: (data) => {
          if (data.status === "paid") {
            toast.success("Payment confirmed — your brief is now unlocked.");
          }
        },
      });
      if (result?.status === "paid") {
        await refreshAccess();
        if (result.tier === "advisory") {
          // Open booking modal so the user can pick a slot now.
          setConsultantOpen(true);
        }
      } else if (result?.status === "expired" || result?.status === "failed") {
        toast.error("Payment did not complete. You can try again from the upgrade card.");
      }
      // Clean URL regardless of outcome
      const next = new URLSearchParams(searchParams);
      next.delete("stripe_session_id");
      setSearchParams(next, { replace: true });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    let secondsTimer = null;

    const stopPolling = () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      if (secondsTimer) { clearInterval(secondsTimer); secondsTimer = null; }
    };

    const loadReadyReport = async () => {
      const a = await getAssessment(id);
      setInsights(a.insights || []);
      setReadiness(a.readiness || {});
      setMode(a.assessment.mode);
      const r = await getReport(id);
      setReport(r);
      setPhase("ready");
    };

    const pollOnce = async () => {
      try {
        const a = await getAssessment(id);
        const status = a.assessment?.report_status;
        if (cancelled) return;
        if (status === "ready" || a.assessment?.report) {
          stopPolling();
          await loadReadyReport();
        } else if (status === "failed") {
          stopPolling();
          setReportError(a.assessment?.report_error || null);
          setPhase("failed");
        }
      } catch (e) {
        console.warn("poll error", e);
      }
    };

    (async () => {
      try {
        const a = await getAssessment(id);
        const status = a.assessment?.report_status;
        if (a.assessment?.report && status !== "generating") {
          await loadReadyReport();
          return;
        }
        if (status === "failed") {
          setReportError(a.assessment?.report_error || null);
          setPhase("failed");
          return;
        }
        // No report yet — show drafting state and poll every 2s
        setPhase("generating");
        secondsTimer = setInterval(() => !cancelled && setPollSeconds((s) => s + 1), 1000);
        pollRef.current = setInterval(pollOnce, 2000);
      } catch (e) {
        // CRITICAL: never bounce back to the form. The user finished their
        // assessment — their data is on the server. Show the Failed phase
        // with a retry button instead.
        console.error("summary initial load failed", e);
        setReportError(
          e?.response?.data?.detail || e?.message || "Could not load your assessment."
        );
        setPhase("failed");
      }
    })();

    return () => { cancelled = true; stopPolling(); };
  }, [id, navigate]);

  const retryGenerate = async () => {
    try {
      setReportError(null);
      setPhase("generating");
      setPollSeconds(0);
      await generateReport(id);
      if (pollRef.current) clearInterval(pollRef.current);
      // Restart the elapsed-seconds counter while retrying.
      const secondsTimer = setInterval(() => setPollSeconds((s) => s + 1), 1000);
      pollRef.current = setInterval(async () => {
        try {
          const a = await getAssessment(id);
          const status = a.assessment?.report_status;
          if (status === "ready" || a.assessment?.report) {
            clearInterval(pollRef.current); pollRef.current = null;
            clearInterval(secondsTimer);
            setInsights(a.insights || []);
            setReadiness(a.readiness || {});
            setMode(a.assessment.mode);
            const r = await getReport(id);
            setReport(r);
            setPhase("ready");
          } else if (status === "failed") {
            clearInterval(pollRef.current); pollRef.current = null;
            clearInterval(secondsTimer);
            setReportError(a.assessment?.report_error || null);
            setPhase("failed");
          }
        } catch (e) { console.warn(e); }
      }, 2000);
    } catch (e) {
      console.error(e);
      toast.error("Could not retry. Please try again.");
      setPhase("failed");
    }
  };

  if (phase === "loading") return <LoadingPhase />;
  if (phase === "generating") return <GeneratingPhase elapsedSeconds={pollSeconds} />;
  if (phase === "failed") return <FailedPhase onRetry={retryGenerate} errorMessage={reportError} onEditAnswers={() => navigate(`/assessment/${id}`)} />;
  if (!report) return <LoadingPhase />;

  const s = report.sections || {};
  const severeCount = (insights || []).filter((i) => i.severity === "severe" || i.severity === "high").length;
  const tabs = REPORT_TABS;
  const unlockPrice = access?.flat_price_usd ? Math.round(access.flat_price_usd) : null;
  const managedPrice = access?.managed_price_usd ? Math.round(access.managed_price_usd) : null;
  // Desktop: open the Action Plan in a focused new tab (no busy right rail).
  // Mobile: navigate in-place (new browser tabs are awkward on phones).
  const openActionPlan = () => {
    if (typeof window !== "undefined" && window.innerWidth >= 1024) {
      window.open(`/plan/${id}`, "_blank", "noopener");
    } else {
      navigate(`/plan/${id}`);
    }
  };
  const scoreCta = access?.paid_managed
    ? { primary: { label: "Open Action Plan", testid: "score-cta-open-plan", icon: "arrow", onClick: openActionPlan } }
    : access?.draft_watermark
      ? {
          primary: { label: `Unlock your clean plan${unlockPrice ? ` — $${unlockPrice}` : ""}`, testid: "score-cta-unlock", icon: "lock", onClick: () => setUpgradeOpen(true) },
          secondary: managedPrice ? { label: `Managed Resilience — $${managedPrice}`, testid: "score-cta-managed", onClick: () => navigate("/pricing") } : null,
        }
      : { primary: managedPrice ? { label: `Managed Resilience — $${managedPrice}`, testid: "score-cta-managed", icon: "arrow", onClick: () => navigate("/pricing") } : null };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />

      <main className="flex-1 max-w-7xl mx-auto w-full px-6 lg:px-10 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* LEFT column: title, actions, report */}
          <div className="lg:col-span-8 min-w-0">
            <div>
              <p className="text-[11px] uppercase tracking-[0.18em] text-teal-700">Risk Summary</p>
              <h1 className="mt-3 font-display text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight">
                Your operational continuity overview
              </h1>
              <p className="mt-3 text-slate-600 max-w-2xl">
                Prepared for <span className="text-slate-900 font-medium">{report.organization}</span> ·{" "}
                <span>{MODE_LABEL[mode] || mode}</span>
                {report.department ? <> · <span>{report.department}</span></> : null}
                {report.industry ? <> · <span>{report.industry}</span></> : null}
                {report.org_size_label ? <> · <span>{report.org_size_label}</span></> : null}
                {" · "}{report.generated_at}
              </p>
            </div>

            {/* Mobile: score + conversion CTA above the report body */}
            <div className="lg:hidden mt-6" data-testid="score-card-mobile">
              <ResilienceScore readiness={readiness} cta={scoreCta} />
            </div>

            {/* One visual primary action */}
            <div className="mt-6">
              <Link
                to={`/export/${id}`}
                data-testid="goto-export-inline"
                className="inline-flex items-center justify-center gap-2 min-h-[44px] px-5 rounded-md bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 transition-colors w-full sm:w-auto"
              >
                <FileText className="h-4 w-4" /> View &amp; Export PDF
              </Link>
            </div>

            {/* Secondary actions, ordered by value */}
            <div className="mt-3 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3">
              <button
                onClick={() => setConsultantOpen(true)}
                data-testid="book-consultant-summary"
                className="inline-flex items-center justify-center gap-2 min-h-[44px] px-4 rounded-md border border-teal-600 text-teal-700 text-sm font-medium hover:bg-teal-50 transition-colors w-full sm:w-auto"
              >
                <Calendar className="h-4 w-4" /> Book a workshop
              </button>
              <button
                onClick={async () => {
                  try {
                    const t = await getFeedbackToken(id);
                    navigate(`/feedback/${t}`);
                  } catch (e) { console.error(e); }
                }}
                data-testid="submit-testimonial-summary"
                className="inline-flex items-center justify-center gap-2 min-h-[44px] px-4 rounded-md border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 transition-colors w-full sm:w-auto"
              >
                <Star className="h-4 w-4 text-amber-500" /> Submit testimonial
              </button>
              <button
                onClick={() => setShareOpen(true)}
                data-testid="share-summary-btn"
                className="inline-flex items-center justify-center gap-2 min-h-[44px] px-4 rounded-md border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 transition-colors w-full sm:w-auto"
              >
                <Share2 className="h-4 w-4" /> Share (30 days)
              </button>
            </div>

            {severeCount > 0 && (
              <div data-testid="severe-banner" className="mt-8 rounded-lg border border-slate-200 rs-banner border-l-4 border-l-red-500 rs-left-red bg-white p-4 flex items-start gap-3">
                <div className="h-2 w-2 rounded-full bg-red-500 mt-2" />
                <div>
                  <p className="text-sm font-medium text-slate-900 rs-txt-primary">
                    {severeCount} elevated risk{severeCount > 1 ? "s" : ""} detected
                  </p>
                  <p className="text-xs text-slate-600 rs-txt-body mt-0.5">
                    Review the items flagged below. Consider engaging a qualified continuity consultant for validation.
                  </p>
                </div>
              </div>
            )}

            {/* Disclaimer collapsed to one line with an expander */}
            <div className="mt-8 rounded-xl border border-slate-200 dark:border-[#2A3438] bg-slate-50 dark:bg-[#192124] px-5 py-4" data-testid="summary-disclaimer-top">
              <button
                onClick={() => setDiscOpen((v) => !v)}
                data-testid="disclaimer-toggle"
                className="w-full flex items-center gap-2 text-left"
                aria-expanded={discOpen}
              >
                <span className="text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-[#94A3B8] shrink-0">Please read first</span>
                {!discOpen && (
                  <span className="flex-1 min-w-0 text-sm text-slate-600 dark:text-[#CBD5E1] truncate">
                    {s.disclaimer || DISCLAIMER_TEXT}
                  </span>
                )}
                <ChevronDown className={`h-4 w-4 text-slate-400 dark:text-[#94A3B8] shrink-0 ml-auto transition-transform ${discOpen ? "rotate-180" : ""}`} />
              </button>
              {discOpen && (
                <p className="mt-3 text-sm text-slate-600 dark:text-[#CBD5E1] leading-relaxed">
                  {s.disclaimer || DISCLAIMER_TEXT}
                </p>
              )}
            </div>

            {/* Tabs + panel */}
            <div className="mt-10">
              <div className="border-b border-slate-200 overflow-x-auto">
                <nav className="flex items-center gap-1 min-w-max" data-testid="report-tabs">
                  {tabs.map((t) => (
                    <button
                      key={t.key}
                      onClick={() => setTab(t.key)}
                      data-testid={`report-tab-${t.key}`}
                      className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${
                        tab === t.key ? "border-teal-600 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </nav>
              </div>

              <section className="mt-8" data-testid={`report-panel-${tab}`}>
                <DisclosureGroup
                  key={tab}
                  idPrefix={`report-${tab}`}
                  sections={buildTabSections(tab, s, { onBook: () => setConsultantOpen(true) })}
                />
              </section>
            </div>
          </div>

          {/* RIGHT rail: Resilience Score at top, aligned with the title (desktop) */}
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-6">
              <SummarySidebar id={id} sections={s} insights={insights} readiness={readiness} access={access} onUpgradeClick={() => setUpgradeOpen(true)} scoreCta={scoreCta} />
            </div>
          </div>
        </div>
      </main>

      <ShareModal assessmentId={id} open={shareOpen} onClose={() => setShareOpen(false)} />
      <BookingModal assessmentId={id} open={consultantOpen} onClose={() => setConsultantOpen(false)} access={access} onAccessRefresh={refreshAccess} />
      <UpgradeModal
        assessmentId={id}
        open={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
        access={access}
        onPaid={() => { refreshAccess(); }}
      />

      <Footer />
    </div>
  );
}
